import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Cookie from 'js-cookie';
import * as Sentry from '@sentry/nextjs';
import Head from 'next/head';
import MinimalLayout from '@c/layouts/MinimalLayout';
import {
  DEFAULT_FORWARD_DEST,
  resolveForwardDest,
} from '@c/authentication/resolveAuthLandingAction';
import {
  RECOVERY_TIMEOUT_MS,
  REFRESH_TIMEOUT_MS,
  resolveRecoveryAction,
  type RecoveryAuthState,
} from '@c/authentication/resolveAuthRecovery';
import { AUTH_COOKIE_OPTS } from '@c/authentication/authCookieOptions';
import { useAuth } from 'context/AuthContext';
import { AUTH } from '@util/firebase';

/**
 * Errors that mean the SESSION is dead rather than the network being bad.
 * Only these justify destroying the cookie — `auth/network-request-failed`
 * must NOT, because the Firebase SDK deliberately does not invalidate a
 * session on network errors, and clearing it would log out a user whose only
 * problem was a dropped connection.
 */
const DEAD_SESSION_CODES = [
  'auth/user-disabled',
  'auth/user-token-expired',
  'auth/user-not-found',
  'auth/invalid-user-token',
];

function isDeadSession(err: unknown): boolean {
  const code = (err as { code?: string })?.code ?? '';
  return DEAD_SESSION_CODES.includes(code);
}

/** Rejects if the refresh has not settled within the budget. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_resolve, reject) =>
      setTimeout(() => reject(new Error('recovery-refresh-timeout')), ms)
    ),
  ]);
}

/**
 * GB #677: silent session recovery.
 *
 * A user returning to a protected route between hour 1 and hour 24 presents an
 * auth cookie whose Firebase ID token has expired. The middleware cannot mint a
 * replacement at the edge, so it used to bounce them to /login — which reads as
 * "you have been logged out" for a session that is entirely recoverable.
 *
 * This route is that recovery. It paints nothing but a spinner, mints a fresh
 * token from the Firebase session the browser still holds, rewrites the cookie,
 * and sends the user where they were originally going. When there is genuinely
 * nothing to recover it hands off to the login form, preserving the
 * destination.
 *
 * Why this is not simply left to /login (which already force-forwards
 * authenticated users, #1449): that path only decides after the user's Firestore
 * profile document has loaded, and a stalled profile load leaves the user on an
 * indefinite spinner. Recovery needs Firebase Auth only, so it is deliberately
 * independent of Firestore.
 *
 * ⚠ REVOCATION WINDOW — the #1435 trade-off is preserved exactly. Minting a
 * token goes through Google, which refuses for a disabled account or a revoked
 * refresh token, so a revoked user cannot recover here and their existing token
 * still expires on its original 1 hour schedule. Do not replace the refresh
 * below with anything that reuses the stale token.
 */
export default function AuthRecoverPage() {
  const router = useRouter();
  const { user } = useAuth();
  const attemptedRef = useRef(false);
  const [timedOut, setTimedOut] = useState(false);

  // Firebase resolves in three stages: undefined (unresolved), null (guest),
  // or a User. Collapsing undefined into "guest" would bounce a recoverable
  // session to the login form during the brief unresolved window.
  const authState: RecoveryAuthState =
    user === undefined ? 'pending' : user === null ? 'guest' : 'authed';

  // A user is looking at a spinner while this runs. If Firebase never resolves
  // (stalled mobile browser, blocked storage), they get a usable login form
  // rather than an indefinite wait.
  useEffect(() => {
    const id = setTimeout(() => setTimedOut(true), RECOVERY_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    // router.query is empty until hydration; without this the destination
    // would be lost and everyone would land on the default.
    if (!router.isReady) return;

    const action = resolveRecoveryAction({
      authState,
      attempted: attemptedRef.current,
      timedOut,
    });
    if (action === 'wait') return;

    const dest = resolveForwardDest(router.query.redirect, DEFAULT_FORWARD_DEST);

    if (action === 'to-login') {
      attemptedRef.current = true;
      // The cookie is stale AND unrecoverable. Leaving it in place means it
      // survives its full 24h life, and every later visit to a protected
      // route pays this whole detour again (both reviews). Clear it so the
      // next visit is a single, honest redirect to the login form.
      Cookie.remove('auth');
      // Preserve the destination so the login form still forwards correctly.
      window.location.replace(
        `/login?redirect=${encodeURIComponent(dest)}`
      );
      return;
    }

    // action === 'refresh-and-forward'.
    // Defensive: the decision core only returns this for authState 'authed',
    // so it is unreachable — it narrows `user` without a non-null assertion
    // and, if it ever were reached, sends the user somewhere usable rather
    // than throwing inside an effect. Mirrors the same guard in
    // AuthenticationForm's forward branch.
    if (!user) {
      attemptedRef.current = true;
      window.location.replace(`/login?redirect=${encodeURIComponent(dest)}`);
      return;
    }

    attemptedRef.current = true;
    const run = async () => {
      try {
        // force = true. A cached token is exactly the expired one that caused
        // this bounce, so anything less than a real refresh would send the
        // user back into the same wall.
        const token = await withTimeout(
          user.getIdToken(true),
          REFRESH_TIMEOUT_MS
        );
        // The await above is a network round trip. A logout in ANOTHER TAB
        // during that window would have cleared the cookie, and writing a
        // freshly-minted 1h token here would resurrect the session the user
        // just ended (QA review). Firebase's auth state is shared across
        // tabs, so re-reading currentUser is the check.
        if (!AUTH.currentUser) {
          Cookie.remove('auth');
          window.location.replace('/login');
          return;
        }
        Cookie.set('auth', token, AUTH_COOKIE_OPTS);
        console.warn(
          '[auth-lifecycle] stale-cookie-recovered',
          JSON.stringify({ to: dest })
        );
        Sentry.addBreadcrumb({
          category: 'auth-lifecycle',
          message: 'stale-cookie-recovered',
          level: 'info',
          data: { to: dest },
        });
        // A FULL navigation, deliberately. We arrived here via a server
        // redirect and the destination is middleware-gated; a document request
        // guarantees the edge sees the cookie we just wrote.
        window.location.replace(dest);
      } catch (err) {
        // Refresh failed — most importantly this is what a revoked or disabled
        // account does, and it must land on the login form, not retry.
        console.warn(
          '[auth-lifecycle] stale-cookie-recovery-failed',
          JSON.stringify({
            message: err instanceof Error ? err.message : 'unknown',
          })
        );
        Sentry.captureMessage('[auth-lifecycle] stale-cookie-recovery-failed', {
          level: 'warning',
          tags: { area: 'auth', kind: 'stale_cookie_recovery_failed' },
        });
        // Destroy the cookie ONLY when the session itself is dead (revoked,
        // disabled, deleted). A timeout or a network failure must leave it
        // alone — the user is fine, their connection is not, and clearing it
        // would turn a bad signal into a real logout.
        if (isDeadSession(err)) {
          Cookie.remove('auth');
        }
        window.location.replace(
          `/login?redirect=${encodeURIComponent(dest)}`
        );
      }
    };
    void run();
  }, [router.isReady, router.query.redirect, authState, timedOut, user]);

  return (
    <>
      <Head>
        <title>Signing you in…</title>
        <meta name="robots" content="noindex,nofollow" />
      </Head>
      <div
        className="flex min-h-[60vh] w-full items-center justify-center"
        role="status"
        aria-live="polite"
      >
        <span className="sr-only">Signing you in</span>
        <div
          aria-hidden="true"
          className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-brand-primary"
        />
      </div>
    </>
  );
}

AuthRecoverPage.getLayout = function getLayout(page: React.ReactElement) {
  return <MinimalLayout>{page}</MinimalLayout>;
};
